import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { CbIconModule, CbModalActions } from '@chubb/ui-components';
import { CbButtonModule } from '@chubb/ui-components/cb-button';
import { CbDropdownMenuModule } from '@chubb/ui-components/cb-dropdown-menu';
import { CbModalModule } from '@chubb/ui-components/cb-modal';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { Coverage } from '../../../interfaces/coverage.model';
import { Customer } from '../../../interfaces/customer.model';
import { BaseWidgetComponent } from '../../../shared/components/base.widget.component';
import { Actions } from '../../../shared/enums/action.enum';
import { BannerAlertType } from '../../../shared/enums/alert-icon.enum';
import { CoverageCode } from '../../../shared/enums/coverage-code.enum';
import { CoverageType } from '../../../shared/enums/coverage-type.enum';
import { RemoveCustomerStatus } from '../../../shared/enums/removeCustomerStatus.enum';
import { MessageBannerService } from '../../../shared/services/message-banner.service';

@Component({
  selector: 'app-progress',
  imports: [
    CommonModule,
    CbButtonModule,
    CbModalModule,
    CbIconModule,
    TieredMenuModule,
    CbDropdownMenuModule,
    MenuModule,
    ButtonModule,
  ],
  templateUrl: './progress.component.html',
  styleUrls: ['./progress.component.scss'],
})
export class ProgressComponent extends BaseWidgetComponent {
  disableModalRemove: boolean = false;
  openResetQuoteConfirmation = false;
  openRemoveCustomerConfirmation = false;
  selectedCustomer?: Customer;
  staticItems: MenuItem[] = [];
  customerItems: MenuItem[] = [];
  menuList: MenuItem[] = [];
  modalActions: CbModalActions = {
    primary: {
      label: '',
    },
  };
  removeStatus: RemoveCustomerStatus = RemoveCustomerStatus.ALLOWED;
  removeCustomerStatusEnums = RemoveCustomerStatus;

  protected readonly _alertBanner: MessageBannerService = inject(MessageBannerService);

  override ngOnInit() {
    super.ngOnInit();
    this._populateMenuCustomerList();
    this._prepareModalActions();
  }

  resetQuote() {
    window.location.reload();
  }

  private _populateMenuCustomerList() {
    // Static menu items
    this.staticItems = [
      {
        label: 'Reset Quote',
        disabled: false,
        command: () => {
          this.handleResetQuoteModal();
        },
      },
      {
        separator: true,
      },
    ];

    // Save updated data
    this._loadCustomerList();
  }

  /**
   * Loads the list of customers and prepares the menu items for removal actions.
   *
   * @returns {void}
   */
  private _loadCustomerList() {
    if (this.sharedPolicyData && Array.isArray(this.sharedPolicyData.customers)) {
      this.customerItems = this.sharedPolicyData.customers.map((c: Customer) => ({
        label: `Remove ${c.firstName} ${c.lastName}`,
        command: () => this.confirmRemoveCustomer(c),
      }));
      // update combined array (if used for menu)
      this.menuList = [...this.staticItems, ...this.customerItems];
    } else {
      this.customerItems = [];
      this.menuList = [...this.staticItems]; // fallback
    }
  }

  handleResetQuoteModal() {
    this.openResetQuoteConfirmation = !this.openResetQuoteConfirmation;
  }

  confirmRemoveCustomer(customer: Customer) {
    this.selectedCustomer = customer;
    this.handleRemoveCustomerModal();
  }

  handleRemoveCustomerModal() {
    // Determine current remove status
    this.removeStatus = this._canRemoveCustomer(this.selectedCustomer);

    // Disable remove button only if removal is *not* allowed
    this.disableModalRemove =
      this.removeStatus !== RemoveCustomerStatus.ALLOWED &&
      this.removeStatus !== RemoveCustomerStatus.CUSTOMER_HAS_DEPENDENCIES;

    // Toggle modal visibility
    this.openRemoveCustomerConfirmation = !this.openRemoveCustomerConfirmation;
  }

  private _prepareModalActions() {
    this.modalActions = {
      primary: {
        label: 'Remove',
      },
      secondary: {
        label: 'Cancel',
      },
    };
  }

  closeRemoveCustomerDialog() {
    this.openRemoveCustomerConfirmation = false;
  }

  /**
   * Removes the expanded accordion state for the specified customer.
   *
   * @param {string} customerNumber - The number identifying the customer whose accordion state should be removed.
   * @returns {void}
   */
  removeCustomerAccordionState(customerNumber?: string) {
    const accordionStates = structuredClone(this._baseDataService.get('expandedCoverages'));
    if (customerNumber != null) {
      this._baseDataService.set(
        'expandedCoverages',
        accordionStates.filter(
          (state: { customer: number; accordions: number[] }) => state.customer !== parseInt(customerNumber),
        ),
      );
    }
  }

  /**
   * Attempts to remove the selected customer from the policy.
   * Displays a message banner to indicate the outcome.
   *
   * @returns {void}
   */
  removeCustomer() {
    if (
      this.removeStatus !== RemoveCustomerStatus.ALLOWED &&
      this.removeStatus !== RemoveCustomerStatus.CUSTOMER_HAS_DEPENDENCIES
    ) {
      // Show message or do nothing
      return;
    }
    // Perform removal
    const successfullyRemoved = this._removeCustomerAndCoverage(this.selectedCustomer);
    if (successfullyRemoved) {
      this._alertBanner.setMessage({
        type: BannerAlertType.success,
        message: `Successfully removed ${this.selectedCustomer?.firstName} ${this.selectedCustomer?.lastName}.`,
        action: Actions.REMOVE_CUSTOMER,
      });
      this._alertBanner.showBanner();
      // Refresh customer list to reflect removal
      this._populateMenuCustomerList();
      this.removeCustomerAccordionState(this.selectedCustomer?.number!);
    } else {
      //TODO: is this ever else block called?
      this._alertBanner.setMessage({
        type: BannerAlertType.error,
        message: `Failed to remove ${this.selectedCustomer?.firstName} ${this.selectedCustomer?.lastName}.`,
        action: Actions.REMOVE_CUSTOMER,
      });
      this._alertBanner.showBanner();
    }
    this.closeRemoveCustomerDialog();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this._baseTriggerPremiumCalculation().catch(error => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      this._utilsService.reAttemptIfTokenExpiry(() => this._baseTriggerPremiumCalculation()).catch((reAttemptError: string) => {
        this.showError(
          `Error performing premium calculation: An error has occurred, please call the Chubb Life team on 0508 464 999 for assistance.`,
        );
      });
    });
  }

  /**
   * Determines whether the selected customer can be removed from the policy,
   * based on current policy rules (such as being the last customer, dependencies, and trauma covers).
   *
   * @param {Customer | undefined} selectedCustomer - The customer to check for removability.
   * @returns {RemoveCustomerStatus} The status indicating if or why removal is allowed or disallowed.
   */
  // eslint-disable-next-line complexity
  private _canRemoveCustomer(selectedCustomer?: Customer): RemoveCustomerStatus {
    const customerNumber = Number(selectedCustomer?.number);
    if (!selectedCustomer || customerNumber == null || !this.sharedPolicyData) {
      return RemoveCustomerStatus.CUSTOMER_NOT_SELECTED;
    }

    const totalCustomers = this.sharedPolicyData.customers?.length;
    if (totalCustomers != null && totalCustomers <= 1) {
      return RemoveCustomerStatus.LAST_CUSTOMER;
    }

    // Count adult covers and child trauma covers
    const customersWithNonTraumaCovers = this.sharedPolicyData.customers?.filter((c: Customer) =>
      this._hasOtherNonTraumaCover(Number(c.number)),
    );

    const adultHasNoTrauma = this.sharedPolicyData.customers?.filter((c: Customer) =>
      this._hasAdultNoTraumaCover(Number(c.number)),
    );

    const childrenTrauma = this.sharedPolicyData.customers?.filter((c: Customer) =>
      this._hasChildTraumaCover(Number(c.number)),
    );

    const adultTraumas = this.sharedPolicyData.customers?.filter((c: Customer) =>
      this._hasAdultTraumaCover(Number(c.number)),
    );

    // If removing last adult with trauma and child trauma exist
    const isLastAdultWithTrauma =
      customersWithNonTraumaCovers &&
      customersWithNonTraumaCovers.length <= 1 &&
      this._hasAdultTraumaCover(customerNumber);

    const childTraumaExists = this.sharedPolicyData.coverages?.some(
      (c: Coverage) =>
        Number(c.customerNumber) !== customerNumber &&
        c.type === CoverageType.TRAUMA_CHILD &&
        c.code === CoverageCode.uTraumaChild,
    );

    if (isLastAdultWithTrauma && childTraumaExists) {
      return RemoveCustomerStatus.LAST_ADULT_WITH_CHILD_TRAUMA;
    }

    // if the selected life insured to remove has no trauma, and the adult trauma cover > 0
    if (adultHasNoTrauma && adultTraumas != null && adultTraumas.length > 0) {
      return RemoveCustomerStatus.ALLOWED;
    }

    if (adultTraumas != null && adultTraumas.length === 1 && childrenTrauma != null && childrenTrauma.length > 0) {
      return RemoveCustomerStatus.CUSTOMER_HAS_DEPENDENCIES;
    }

    return RemoveCustomerStatus.ALLOWED;
  }

  /**
   * Removes the specified customer and associated coverages from the current policy data.
   * Handles removal of trauma covers and dependencies for adult and child customers,
   * and updates policy data accordingly.
   *
   * @param {Customer | undefined} customer - The customer to remove from the policy.
   * @returns {boolean} True if the customer and their coverages were successfully removed, otherwise false.
   */
  // eslint-disable-next-line complexity
  private _removeCustomerAndCoverage(customer?: Customer): boolean {
    const customerNumber = Number(customer?.number);

    // Count adult trauma covers
    const traumaAdults = this.sharedPolicyData?.customers?.filter((c: Customer) =>
      this._hasAdultTraumaCover(Number(c.number)),
    );

    // Check if current customer has trauma cover
    const currentHasTrauma = this._hasAdultTraumaCover(customerNumber);

    if (traumaAdults != null && traumaAdults.length > 1 && currentHasTrauma) {
      // Remove the customer
      const newCustomers = this.sharedPolicyData?.customers?.filter(
        (c: Customer) => Number(c.number) !== customerNumber,
      );
      if (this.sharedPolicyData && newCustomers) {
        this.sharedPolicyData.customers = newCustomers;
      }
    } else if (currentHasTrauma) {
      // Remove child trauma covers if removing an adult with dependencies
      const newCustomers = this.sharedPolicyData?.customers?.filter(
        (c: Customer) => !this._hasChildTraumaCover(Number(c.number)),
      );
      if (this.sharedPolicyData && newCustomers) {
        this.sharedPolicyData.customers = newCustomers;
      }
      const newCoverages = this.sharedPolicyData?.coverages?.filter(
        (c: Coverage) => !this._hasChildTraumaCover(Number(c.customerNumber)),
      );
      if (this.sharedPolicyData && newCoverages) {
        this.sharedPolicyData.coverages = newCoverages;
      }
    }

    // Remove the customer
    const newCustomers = this.sharedPolicyData?.customers?.filter((c: Customer) => Number(c.number) !== customerNumber);
    if (this.sharedPolicyData && newCustomers) {
      this.sharedPolicyData.customers = newCustomers;
    }

    // Remove coverages for this customer
    const newCoverages = this.sharedPolicyData?.coverages?.filter(
      (c: Coverage) => Number(c.customerNumber) !== customerNumber,
    );
    if (this.sharedPolicyData && newCoverages) {
      this.sharedPolicyData.coverages = newCoverages;
    }

    return true;
  }

  // Helper: Check if customer has any other Cover
  private _hasOtherNonTraumaCover(customerNumber: number): boolean {
    return (
      this.sharedPolicyData?.coverages?.some(
        (c: Coverage) =>
          Number(c.customerNumber) === customerNumber &&
          c.type !== CoverageType.TRAUMA &&
          c.code !== CoverageCode.uTraumaChild,
      ) ?? false
    );
  }

  // Helper: Check if customer has an Adult Trauma Cover
  private _hasAdultTraumaCover(customerNumber: number): boolean {
    return (
      this.sharedPolicyData?.coverages?.some(
        (c: Coverage) =>
          Number(c.customerNumber) === customerNumber &&
          c.type === CoverageType.TRAUMA &&
          c.code !== CoverageCode.uTraumaChild,
      ) ?? false
    );
  }

  // Helper: Check if customer has an Adult has no Trauma Cover
  private _hasAdultNoTraumaCover(customerNumber: number): boolean {
    return (
      this.sharedPolicyData?.coverages?.some(
        (c: Coverage) => Number(c.customerNumber) === customerNumber && c.type !== CoverageType.TRAUMA,
      ) ?? false
    );
  }

  // Helper: Check if customer has a Child Trauma Cover
  private _hasChildTraumaCover(customerNumber: number): boolean {
    return (
      this.sharedPolicyData?.coverages?.some(
        (c: Coverage) =>
          Number(c.customerNumber) === customerNumber &&
          c.type === CoverageType.TRAUMA_CHILD &&
          c.code === CoverageCode.uTraumaChild,
      ) ?? false
    );
  }
}
